/**
 * Mockup→Live Phase 5 — Bulk-Action-Bar.
 *
 * Erscheint über der Vordienst-Zeilenliste, sobald mindestens eine
 * Zeile per Kompakt-Multi-Select-Checkbox ausgewählt wurde. Bietet
 * Massen-Editier-Aktionen für schnelle Sachbearbeiter-Workflows.
 *
 * Alle Aktionen delegieren an `onBulkApply(actionKey, params)`, das
 * in `StepThree.jsx` implementiert ist und die selektierten Indices
 * mit `updateRow(idx, next)` iterativ aktualisiert. Auto-Recalc-Hook
 * (Phase 1) läuft danach EINMAL, weil alle setRows-Calls in derselben
 * React-Batch landen.
 */
import React, { useState } from "react";
import { Check, X, Percent, CheckSquare } from "@phosphor-icons/react";

export default function BulkActionBar({
  selectedCount,
  onClearSelection,
  onBulkApply,
}) {
  const [factorInput, setFactorInput] = useState("50");
  const [showFactorDialog, setShowFactorDialog] = useState(false);

  const applyFactor = () => {
    const pct = Math.max(0, Math.min(100, Number(factorInput) || 0));
    onBulkApply("set_faktor", { faktor: pct / 100 });
    setShowFactorDialog(false);
  };

  return (
    <div
      className="mb-3 flex flex-wrap items-center gap-2 border border-brand-300 bg-brand-50 px-3 py-2 rounded-sm text-[12px] text-ink-900"
      data-testid="tool-step3-bulk-bar"
      role="region"
      aria-label="Massen-Aktionen"
    >
      <span
        className="font-semibold text-brand-800"
        data-testid="tool-step3-bulk-count"
      >
        {selectedCount === 1
          ? "1 Zeile ausgewählt"
          : `${selectedCount} Zeilen ausgewählt`}
      </span>
      <span className="text-ink-300">|</span>
      {/* Anrechnen aktivieren */}
      <button
        type="button"
        onClick={() => onBulkApply("set_anrechenbar", { anrechenbar: true })}
        data-testid="tool-step3-bulk-anr-on"
        className="inline-flex items-center gap-1 rounded-sm border border-status-aktiv-border bg-white px-2 py-1 text-[11px] font-medium text-status-aktiv-text hover:bg-status-aktiv-bg transition-crisp"
        title="Bei allen ausgewählten Zeilen Anrechnen aktivieren"
      >
        <Check size={12} weight="bold" />
        Anrechnen an
      </button>
      {/* Anrechnen deaktivieren */}
      <button
        type="button"
        onClick={() => onBulkApply("set_anrechenbar", { anrechenbar: false })}
        data-testid="tool-step3-bulk-anr-off"
        className="inline-flex items-center gap-1 rounded-sm border border-warm-300 bg-white px-2 py-1 text-[11px] font-medium text-warm-700 hover:bg-warm-100 transition-crisp"
        title="Bei allen ausgewählten Zeilen Anrechnen deaktivieren"
      >
        <X size={12} weight="bold" />
        Anrechnen aus
      </button>
      {/* Faktor auf X % */}
      {!showFactorDialog ? (
        <button
          type="button"
          onClick={() => setShowFactorDialog(true)}
          data-testid="tool-step3-bulk-faktor-open"
          className="inline-flex items-center gap-1 rounded-sm border border-amber-400 bg-white px-2 py-1 text-[11px] font-medium text-amber-900 hover:bg-amber-100 transition-crisp"
          title="Anrechnungsfaktor bei allen ausgewählten Zeilen setzen"
        >
          <Percent size={12} weight="bold" />
          Faktor …
        </button>
      ) : (
        <span
          className="inline-flex items-center gap-1 rounded-sm border border-amber-400 bg-white px-1.5 py-0.5"
          data-testid="tool-step3-bulk-faktor-dialog"
        >
          <input
            type="number"
            min={0}
            max={100}
            step={5}
            value={factorInput}
            onChange={(e) => setFactorInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFactor();
              if (e.key === "Escape") setShowFactorDialog(false);
            }}
            className="w-14 bg-transparent px-1 py-0.5 text-right text-[13px] tabular-nums text-amber-900 font-semibold focus:outline-none"
            autoFocus
            data-testid="tool-step3-bulk-faktor-input"
            aria-label="Anrechnungsfaktor in Prozent"
          />
          <span className="text-[11px] font-medium text-amber-700 select-none">%</span>
          <button
            type="button"
            onClick={applyFactor}
            data-testid="tool-step3-bulk-faktor-apply"
            className="ml-1 inline-flex items-center rounded-sm border border-amber-500 bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white hover:bg-amber-600 transition-crisp"
          >
            Anwenden
          </button>
          <button
            type="button"
            onClick={() => setShowFactorDialog(false)}
            data-testid="tool-step3-bulk-faktor-cancel"
            aria-label="Abbrechen"
            className="text-warm-500 hover:text-warm-700 p-0.5"
          >
            <X size={12} weight="bold" />
          </button>
        </span>
      )}
      {/* Alle als geprüft markieren */}
      <button
        type="button"
        onClick={() => onBulkApply("mark_reviewed", {})}
        data-testid="tool-step3-bulk-mark-reviewed"
        className="inline-flex items-center gap-1 rounded-sm border border-emerald-400 bg-white px-2 py-1 text-[11px] font-medium text-emerald-800 hover:bg-emerald-50 transition-crisp"
        title={`Alle ausgewählten Zeilen als „geprüft & bestätigt" markieren (löscht offene Auslese-Warnungen)`}
      >
        <CheckSquare size={12} weight="bold" />
        Als geprüft markieren
      </button>
      <span className="text-ink-300">|</span>
      {/* Auswahl aufheben */}
      <button
        type="button"
        onClick={onClearSelection}
        data-testid="tool-step3-bulk-clear"
        className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-[11px] font-medium text-warm-600 hover:bg-white hover:text-ink-800 transition-crisp"
        title="Auswahl aufheben (ausgewählte Zeilen bleiben unverändert)"
      >
        <X size={12} weight="bold" />
        Auswahl aufheben
      </button>
    </div>
  );
}
